import { useState } from "react";

import { Card } from "src/components/card";
import {
  Box,
  Row,
  Skeleton,
  SkeletonBox,
  Text,
  IconButton,
  Tooltip,
  DownloadIcon,
  ImageIcon,
  HtMedia,
  isVideoSrc,
} from "@hightouch/htui";
import { FullScreenImageOverlay } from "src/components/llm/components/full-screen-image-overlay";
import { downloadBlob } from "src/utils/download";
import { useSaveArtifactImage } from "src/components/llm/content-images/use-save-artifact-image";
import { ContentImageSource } from "@hightouch/lib/content/types";

export const MarkdownImage = ({
  img,
}: {
  img: { src?: string; alt?: string };
}) => {
  return <ImageCard img={img} maxHeight="200px" expandable />;
};

const getFileNameFromUrl = (src: string, alt?: string): string => {
  if (alt) return alt;
  try {
    const url = new URL(src);
    const pathname = url.pathname;
    const lastSegment = pathname.split("/").pop();
    if (lastSegment) return lastSegment;
  } catch {
    // ignore
  }
  return "image.png";
};

const downloadImage = async (src: string, fileName: string) => {
  try {
    const response = await fetch(src);
    if (!response.ok) {
      throw new Error("Download failed");
    }
    const blob = await response.blob();
    downloadBlob(blob, fileName);
  } catch {
    window.open(src, "_blank");
  }
};

const ImageActions = ({ src, alt }: { src: string; alt?: string }) => {
  const fileName = getFileNameFromUrl(src, alt);
  const { saveToContentLibrary, isSaving } = useSaveArtifactImage();

  const handleSaveToLibrary = async () => {
    // Note, we don't track which MarkdownImages that we save to the ContentLibrary. This
    // may lead to duplicates in the library. We've accepted that this is okay for now.
    await saveToContentLibrary({
      url: src,
      fileName,
      sourceType: ContentImageSource.AGENT_CHAT_OUTPUT,
      triggerTagging: true,
    });
  };

  return (
    <Row gap={0.5} bg="white" borderRadius="md" boxShadow="sm" p={1}>
      <Tooltip message="Download image" placement="bottom">
        <IconButton
          aria-label="Download image"
          icon={DownloadIcon}
          size="sm"
          variant="tertiary"
          onClick={() => downloadImage(src, fileName)}
        />
      </Tooltip>
      <Tooltip message="Save to library" placement="bottom">
        <IconButton
          aria-label="Save to content library"
          icon={ImageIcon}
          size="sm"
          variant="tertiary"
          isLoading={isSaving}
          isDisabled={isSaving}
          onClick={handleSaveToLibrary}
        />
      </Tooltip>
    </Row>
  );
};

const ImageCard = ({
  img,
  maxHeight,
  expandable = false,
}: {
  img: { src?: string; alt?: string };
  maxHeight: string;
  expandable?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <>
      <Box
        position="relative"
        width="fit-content"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Card
          p={0.5}
          width="fit-content"
          onClick={
            expandable && !isLoading && !error
              ? () => setExpanded(true)
              : undefined
          }
          maxHeight="auto"
        >
          {isLoading && (
            <Skeleton isLoading={isLoading}>
              <SkeletonBox height={maxHeight} width={maxHeight} />
            </Skeleton>
          )}
          {error && <Text color="text.danger">Error loading image</Text>}
          <HtMedia
            borderRadius="md"
            src={img.src ?? ""}
            alt={img.alt ?? ""}
            maxHeight={maxHeight}
            minWidth="100px"
            objectFit="contain"
            height={isLoading ? 0 : "auto"}
            opacity={isLoading ? 0 : 1}
            transition="opacity 0.3s ease-in-out"
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setError(true);
              setIsLoading(false);
            }}
            video={{ playback: expandable ? "none" : "click" }}
            style={{ maxHeight }}
          />
        </Card>
        {!isLoading && !error && img.src && (
          <Box
            position="absolute"
            top={2}
            right={2}
            visibility={isHovered ? "visible" : "hidden"}
            opacity={isHovered ? 0.8 : 0}
            transition="opacity 0.2s, visibility 0.2s"
          >
            <ImageActions src={img.src} alt={img.alt} />
          </Box>
        )}
      </Box>
      <FullScreenImageOverlay
        imageSrc={img.src ?? ""}
        fileName={img.alt ?? ""}
        isOpen={expanded}
        onClose={() => setExpanded(false)}
        maxHeight={isVideoSrc(img.src) ? "500px" : undefined}
      />
    </>
  );
};
